import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  LOCALE_ID,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withPreloading,
} from '@angular/router';

import {
  AttachmentBlobService,
  AttachmentEnvToken,
} from '@plugins/client/attachment/attachment-blob.service';
import { ImageResizeService } from '@plugins/client/attachment/image-resize.service';
import { authInterceptorFn, AuthModule } from '@plugins/client/auth';
import { languageHeadersInterceptorFn } from '@plugins/client/interceptors/language-headers.interceptor';
import {
  jsonDateInterceptorFn,
  JsonDateModule,
} from '@plugins/client/json-date';
import { MultilanguageService } from '@plugins/client/multilanguage/multilanguage.service';
import { AdminPreloadStrategy } from '@plugins/client/routing/admin-preload-strategy';
import { provideAppVersion } from '@plugins/client/tokens/app-version.token';
import {
  TRANSLATE_CONFIG,
  TranslateConfigToken,
} from '@plugins/client/translate/services';
import { MiddlewareSdkModule } from '@plugins/middleware-sdk';

import { environment } from '../environments/environment';

import { routes } from './app.routes';

/**
 * `LOCALE_ID` must resolve to a plain string, otherwise Angular pipes such as
 * `DatePipe` fail with "locale.toLowerCase is not a function".
 * The async SDK based provider therefore cannot be used here.
 */
function resolveLocaleId(): string {
  const lang = (navigator.language ?? '').split('-').at(0);
  return environment.languages.includes(lang) ? lang : environment.languages[0];
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withPreloading(AdminPreloadStrategy),
      withComponentInputBinding(),
    ),
    provideHttpClient(
      withInterceptors([
        authInterceptorFn,
        languageHeadersInterceptorFn,
        jsonDateInterceptorFn,
      ]),
    ),
    importProvidersFrom(MiddlewareSdkModule),
    importProvidersFrom(AuthModule.forRoot()),
    importProvidersFrom(JsonDateModule.configure({ paths: ['/api/'] })),
    { provide: LOCALE_ID, useFactory: resolveLocaleId },
    provideAppVersion(),
    MultilanguageService,
    ImageResizeService,
    AttachmentBlobService,
    { provide: AttachmentEnvToken, useValue: environment },
    {
      provide: TRANSLATE_CONFIG,
      useValue: {
        languages: environment.languages,
        fallbackLanguage: environment.languages[0],
        useFallbackLanguageOnMissing: true,
      } satisfies TranslateConfigToken,
    },
  ],
};
